import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDate, formatTime, STATUS_LABELS, STATUS_CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretLeft, CurrencyCircleDollar, Envelope, FilePdf, Phone, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

export default function StudentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "cash", date: new Date().toISOString().slice(0, 10), note: "" });
  const now = new Date();
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);

  async function load() {
    const [s, l, p] = await Promise.all([
      api.get(`/students/${id}`),
      api.get(`/lessons`, { params: { student_id: id } }),
      api.get(`/payments`, { params: { student_id: id } }),
    ]);
    setStudent(s.data); setLessons(l.data); setPayments(p.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const c = user?.currency || "TRY";
  const totalEarned = lessons.filter((l) => l.status === "completed" || l.status === "cancelled_paid").reduce((s, l) => s + (l.amount || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const balance = totalEarned - totalPaid;
  const completed = lessons.filter((l) => l.status === "completed").length;

  async function addPayment() {
    try {
      await api.post("/payments", {
        student_id: id,
        amount: Number(payForm.amount),
        method: payForm.method,
        date: new Date(payForm.date).toISOString(),
        note: payForm.note,
      });
      toast.success("Ödeme eklendi.");
      setPayOpen(false);
      setPayForm({ amount: "", method: "cash", date: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch { toast.error("Hata."); }
  }

  async function downloadReport() {
    try {
      const res = await api.get(`/reports/monthly-pdf`, {
        params: { student_id: id, year: reportYear, month: reportMonth },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapor_${student?.name}_${reportYear}_${String(reportMonth).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Rapor oluşturulamadı."); }
  }

  if (!student) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="student-detail-page">
      <Link to="/ogrenciler" className="text-sm text-[#3A5A40] hover:underline inline-flex items-center gap-1">
        <CaretLeft size={14} /> Öğrencilere dön
      </Link>

      <div className="bg-white border border-border rounded-lg p-6 flex flex-col md:flex-row md:items-center gap-6">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ background: student.color || "#3A5A40" }}>
          {student.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold truncate" data-testid="student-detail-name">{student.name}</h1>
          {student.parent_name && <div className="text-muted-foreground text-sm mt-1">Veli: {student.parent_name}</div>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2">
            {student.parent_email && <span className="inline-flex items-center gap-1"><Envelope size={14} /> {student.parent_email}</span>}
            {student.parent_phone && <span className="inline-flex items-center gap-1"><Phone size={14} /> {student.parent_phone}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Saatlik ücret" value={formatCurrency(student.hourly_rate, c)} />
        <Stat label="Tamamlanan ders" value={completed} />
        <Stat label="Toplam hak edilen" value={formatCurrency(totalEarned, c)} />
        <Stat label="Bakiye" value={formatCurrency(balance, c)} tone={balance > 0 ? "accent" : "success"} />
        {student.package_total > 0 && (
          <div className="col-span-2 lg:col-span-4 bg-white border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Paket ilerleme</div>
              <div className="text-sm text-muted-foreground">{completed}/{student.package_total} ders</div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-[#3A5A40]" style={{ width: `${Math.min(100, (completed / student.package_total) * 100)}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">Kalan: {Math.max(0, student.package_total - completed)} ders</div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lessons */}
        <div className="bg-white border border-border rounded-lg p-5">
          <h2 className="font-display text-lg font-bold mb-3">Dersler</h2>
          {lessons.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Henüz ders yok.</div>
          ) : (
            <ul className="divide-y divide-border max-h-96 overflow-y-auto">
              {lessons.map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{formatDate(l.start_time)} · {formatTime(l.start_time)}–{formatTime(l.end_time)}</div>
                    <div className="text-xs text-muted-foreground">{l.subject || "—"}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_CLASSES[l.status]}`}>
                    {STATUS_LABELS[l.status]}
                  </span>
                  <div className="font-mono text-sm w-20 text-right">{formatCurrency(l.amount, c)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payments */}
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold">Ödemeler</h2>
            <Button size="sm" onClick={() => setPayOpen(true)} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="student-add-payment-button">
              <Plus size={14} className="mr-1" /> Ödeme Ekle
            </Button>
          </div>
          {payments.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Kayıtlı ödeme yok.</div>
          ) : (
            <ul className="divide-y divide-border max-h-96 overflow-y-auto">
              {payments.map((p) => (
                <li key={p.id} className="flex items-center gap-3 py-3 text-sm">
                  <CurrencyCircleDollar size={18} className="text-[#2E7D32]" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{formatDate(p.date)} · {p.method}</div>
                    {p.note && <div className="text-xs text-muted-foreground truncate">{p.note}</div>}
                  </div>
                  <div className="font-mono text-sm text-[#2E7D32] font-semibold">{formatCurrency(p.amount, c)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* PDF report */}
      <div className="bg-white border border-border rounded-lg p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold">Aylık PDF Raporu</h2>
            <p className="text-sm text-muted-foreground">Veli için hazır ders ve ödeme çizelgesini indirin.</p>
          </div>
          <div className="flex gap-2">
            <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_TR.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(reportYear)} onValueChange={(v) => setReportYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadReport} className="bg-[#D97757] hover:bg-[#BF6446] text-white" data-testid="download-pdf-button">
              <FilePdf size={16} className="mr-1" weight="duotone" /> İndir
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Ödeme ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Tutar ({c})</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-form-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Tarih</Label>
              <Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Yöntem</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="bank">Banka / Havale</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Not</Label>
              <Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Vazgeç</Button>
            <Button onClick={addPayment} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="payment-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === "accent" ? "text-[#D97757]" : tone === "success" ? "text-[#2E7D32]" : "text-foreground";
  return (
    <div className="bg-white border border-border rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-xl md:text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}
